'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import HeroBulletTicker from '@/app/hero-home-section/components/HeroBulletTicker';

interface AboutInteractiveProps {
  lang?: 'en' | 'de';
}

const AboutInteractive = ({ lang = 'en' }: AboutInteractiveProps) => {
  const [currentLang, setCurrentLang] = useState(lang);

  useEffect(() => {
    // Update language if prop changes
    setCurrentLang(lang);

    // Listen for language changes from Header component
    const handleLanguageChange = (event: CustomEvent<{language: 'en' | 'de';}>) => {
      setCurrentLang(event.detail.language);
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, [lang]);

  const handleCTAClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const contactSection = document.querySelector('#contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <section id="about" className="relative pt-32 pb-8">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Bilingual Headings */}
          <h2
            data-lang="en"
            className="text-5xl font-bold text-center mb-4"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff',
              display: currentLang === 'en' ? 'block' : 'none'
            }}>

            About
          </h2>
          <h2
            data-lang="de"
            className="text-5xl font-bold text-center mb-4"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff',
              display: currentLang === 'de' ? 'block' : 'none'
            }}>

            Über&nbsp;uns
          </h2>

          <h3
            data-lang="en"
            className="text-3xl font-semibold text-center mb-16"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#16c55f',
              display: currentLang === 'en' ? 'block' : 'none'
            }}>

            Meet the Founder
          </h3>
          <h3
            data-lang="de"
            className="text-3xl font-semibold text-center mb-16"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#16c55f',
              display: currentLang === 'de' ? 'block' : 'none'
            }}>Lerne den Gründer kennen


          </h3>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-[350px_1fr] gap-12 items-start mb-8">
            {/* Left column - Founder Photo */}
            <div className="flex flex-col items-center gap-6">
              <div
                className="relative w-72 rounded-2xl overflow-hidden"
                style={{
                  aspectRatio: '3 / 4',
                  border: '3px solid #00b8ff',
                  boxShadow: '0 0 20px rgba(0, 184, 255, 0.5)'
                }}>

                <Image
                  src="/assets/images/Stefan_4-1772985848516.jpg"
                  alt="Stefan Artmann portrait"
                  fill
                  priority
                  className="object-cover object-top"
                  priority />

              </div>
              
              {/* Contact Stefan CTA Button */}
              <Link href="/contact-section">
                <button
                  data-lang="en"
                  className="px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                  style={{
                    fontFamily: '"Exo 2", sans-serif',
                    backgroundColor: 'transparent',
                    border: '2px solid #00b8ff',
                    color: '#00b8ff',
                    display: currentLang === 'en' ? 'inline-block' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#00b8ff';
                    e.currentTarget.style.color = '#031b3a';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 184, 255, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#00b8ff';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                  Contact Stefan
                </button>
              </Link>
              <Link href="/contact-section">
                <button
                  data-lang="de"
                  className="px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                  style={{
                    fontFamily: '"Exo 2", sans-serif',
                    backgroundColor: 'transparent',
                    border: '2px solid #00b8ff',
                    color: '#00b8ff',
                    display: currentLang === 'de' ? 'inline-block' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#00b8ff';
                    e.currentTarget.style.color = '#031b3a';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 184, 255, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#00b8ff';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                  Stefan kontaktieren
                </button>
              </Link>
            </div>

            {/* Right column - Biography */}
            <div className="space-y-6 mt-0">
              <p
                data-lang="en"
                className="text-lg leading-relaxed text-center"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>Stefan Artmann founded Planet Pulse Studios in December 2025 to bring an AI‑First vision to marketing, digital projects and mobile applications. By harnessing AI prompting, agents and automation, he accelerates research, ideation, content creation and production workflows while ensuring that quality and brand integrity remain uncompromised. As AI models and systems change the business landscape very quickly, Stefan believes it is imperative to learn brand new optimization strategies like AEO and GEO as well as always stay tuned for new tools and how AI is changing what we think is possible. To stay visible and on top of the markets with the help of AI is the key to big advantages. The core business concept of Planet Pulse Studios is to leverage the productive synergies of Artificial Intelligence and the unique experience and expertise of Human Intelligence in the best possible way.
              </p>
              <p
                data-lang="en"
                className="text-lg leading-relaxed text-center"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>With an impressive academic background in Political Science, Media, History and Cognitive Science Stefan has built over the past 15 years a professional reputation as a Senior Marketing and Product Growth Expert, advising teams for browser and mobile strategy games and overseeing market entry plans for apps and features. His deep experience spans reporting and analytics, stakeholder communication, social media strategy, ASO, SEO, CRO, monetization, brand and performance marketing. Known for combining AI tools and creative concepts with hands‑on ad production and clear performance KPIs, he excels at orchestrating cross‑functional workflows between marketing, product, app design, business intelligence and creative teams to deliver campaigns, features and live projects that drive core business metrics.
              </p>
              <p
                data-lang="de"
                className="text-lg leading-relaxed text-center mt-0 pt-0"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>Stefan Artmann gründete Planet Pulse Studios im Dezember 2025, um eine KI-First Vision für Marketing, digitale Projekte und mobile Anwendungen zu verwirklichen. Durch den Einsatz von KI Prompting, Agenten und Automatisierung beschleunigt er Recherche, Ideenfindung, Content Erstellung und Produktionsabläufe, ohne dabei Qualität und Marken Integrität zu gefährden. Da KI-Modelle und -Systeme die Geschäftswelt sehr schnell verändern, ist Stefan überzeugt, dass es unerlässlich ist, brandneue Optimierungsstrategien wie AEO und GEO zu erlernen und stets über neue Tools und die Möglichkeiten, die KI eröffnet, informiert zu bleiben. Mit Hilfe von KI sichtbar und an der Spitze der Märkte zu bleiben, ist der Schlüssel zu großen Vorteilen. Das zentrale Geschäftskonzept von Planet Pulse Studios besteht darin, die Synergien von Künstlicher Intelligenz und der einzigartigen Erfahrung Menschlicher Intelligenz bestmöglich produktiv zu nutzen.
              </p>
              <p
                data-lang="de"
                className="text-lg leading-relaxed text-center"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>Mit einem beeindruckenden akademischen Hintergrund in Politikwissenschaftenen, Medien, Geschichte und Kognitionswissenschaftenen hat sich Stefan in den vergangenen 15 Jahren als Senior Experte für Marketing und Produktwachstum beruflich etabliert. Er beriet Teams für Browser und Mobile Strategiespiele und setzte erfolgreich Strategien für den Markteintritt von Apps und Features um. Seine umfassende Erfahrung erstreckt sich über Reporting und Analytik, Stakeholder Kommunikation, Social Media Strategien, ASO, SEO, CRO, Monetarisierung, Markenbildung und Performance Marketing. Bekannt dafür, KI‑Tools und kreative Konzepte mit praktischer Werbemittel Produktion und klaren Performance KPIs zu verbinden, orchestrierte er erfolgreich funktionsübergreifende Workflows zwischen Marketing, Produkt, App Design, Business Intelligence und Creative Teams, um Kampagnen, Features und Live Projekte zu liefern, die zentrale Geschäftskennzahlen vorantreiben.
              </p>

              {/* Strengths List */}
              




































































































            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="relative py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Bilingual Team Headings */}
          <h2
            data-lang="en"
            className="text-5xl font-bold text-center mb-4"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff',
              display: currentLang === 'en' ? 'block' : 'none'
            }}>

            Team
          </h2>
          <h2
            data-lang="de"
            className="text-5xl font-bold text-center mb-4"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff',
              display: currentLang === 'de' ? 'block' : 'none'
            }}>

            Unser&nbsp;Team
          </h2>

          <p
            data-lang="en"
            className="text-3xl font-semibold text-center mb-16"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#16c55f',
              display: currentLang === 'en' ? 'block' : 'none'
            }}>

            Meet the AI Agents behind Planet Pulse Studios.
          </p>
          <p
            data-lang="de"
            className="text-3xl font-semibold text-center mb-16"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#16c55f',
              display: currentLang === 'de' ? 'block' : 'none'
            }}>

            Lerne die KI‑Agenten hinter Planet Pulse Studios kennen.
          </p>

          {/* Team Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* First Card - Nova Quinn Sterling */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Nova's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-full overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/0b348bef-3958-45f6-8ef6-7db2a35bc7b5-1767042636671.png"
                    alt="Nova Quinn Sterling portrait"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Nova's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Nova Quinn<br />(App Design)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Nova Quinn<br />(App Design)
              </h3>

              {/* Nova's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                App Design Engineer & Rocket.new Expert
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                App‑Design‑Ingenieurin & Rocket.new‑Expertin
              </p>

              {/* Nova's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Nova Quinn designs responsive, bug‑free apps using Rocket.new, conducts competitor research and delivers launch and growth strategies. She generates ready‑to‑use code prompts and debugging instructions, ensuring your app launches smoothly.
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Nova Quinn entwirft reaktionsschnelle, fehlerfreie Apps mit Rocket.new, recherchiert Wettbewerber und entwickelt Launch‑ und Wachstumsstrategien. Sie erstellt sofort einsetzbare Code‑Prompts und Debugging‑Anleitungen, damit Ihre App reibungslos startet.
              </p>

            </div>

            {/* Second Card - Astro Stone */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Astro's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-lg overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/2395414e-4624-4f17-9314-d4d84c829181-1767044274550.png"
                    alt="Astro Stone portrait - Media Planner with futuristic digital advertising interface background"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Astro's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Astro Stone<br />(Media Plan)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  color: '#16c55f',
                  fontFamily: '"Exo 2", sans-serif',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Astro Stone<br />(Media&nbsp;Plan)
              </h3>

              {/* Astro's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Paid Media Campaign Architect
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>Paid‑Media‑Kampagnen‑Architekt


              </p>

              {/* Astro's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>Astro Stone prepares and designs multi‑platform advertising campaigns across Google, Facebook/Instagram, TikTok and LinkedIn. He suggests tracking, builds audiences, creates assets, researches keywords and writes persuasive ad copy for both B2B and B2C objectives.


              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>Astro Stone konzipiert und entwirft Multi‑Plattform‑Werbekampagnen auf Google, Facebook/Instagram, TikTok und LinkedIn. Er richtet Tracking ein, erstellt Zielgruppen, recherchiert Keywords und verfasst überzeugende Anzeigentexte für B2B‑ und B2C‑Ziele.


              </p>

            </div>

            {/* Third Card - Orion Byte */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Orion's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-lg overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/36d82511-cb7e-47c8-8466-b9d9c6f3c41d-1767044887940.png"
                    alt="Orion Byte portrait - Campaign Analysis & Optimization Specialist with data visualization interface"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Orion's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Orion Byte<br />(Campaign Tweak)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Orion Byte<br />(Campaign&nbsp;Tweak)
              </h3>

              {/* Orion's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Campaign Analysis & Optimisation Specialist
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Kampagnenanalyse‑ und Optimierungsspezialist
              </p>

              {/* Orion's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Orion Byte evaluates marketing performance across paid, organic and email channels, identifies trends and proposes optimisations. He produces clear reports, A/B test plans and actionable recommendations to improve ROI.
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Orion Byte analysiert Marketing‑Leistungen über Paid‑, organische und E‑Mail‑Kanäle hinweg, erkennt Trends und schlägt Optimierungen vor. Er erstellt klare Berichte, A/B‑Testpläne und umsetzbare Empfehlungen zur Verbesserung des ROI.
              </p>

            </div>

            {/* Fourth Card - Luna Cipher */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Luna's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-full overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/c4d31286-a69d-4122-9436-259dc3f09010-1767045070544.png"
                    alt="Luna Cipher portrait - Landing Page Optimiser with futuristic holographic interface background"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Luna's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Luna Cipher<br />(Funnel Up)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Luna Cipher<br />(Funnel&nbsp;Up)
              </h3>

              {/* Luna's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Landing Page Optimiser
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Landingpage‑Optimiererin
              </p>

              {/* Luna's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Luna Cipher audits landing pages for design, copy and performance. She suggests improvements to layout, messaging, trust elements and mobile responsiveness to boost conversions for both B2B and B2C audiences.
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Luna Cipher prüft Landingpages auf Design, Text und Performance. Sie schlägt Verbesserungen bei Layout, Botschaft, Vertrauenselementen und mobiler Darstellung vor, um die Conversion bei B2B‑ und B2C‑Zielgruppen zu steigern.
              </p>

            </div>

            {/* Fifth Card - Zenith Asha */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Zenith's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-full overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/4d872fdf-b7f0-474a-8a9c-4d991acadc20-1767045336753.png"
                    alt="Zenith Asha portrait - App Store Optimization Expert with futuristic tech interface background"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Zenith's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Zenith Asha<br />(ASO Boost)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Zenith Asha<br />(ASO&nbsp;Boost)
              </h3>

              {/* Zenith's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                App Store Optimisation Expert
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>App‑Store‑Optimierungs‑Expertin


              </p>

              {/* Zenith's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Zenith Asha improves your app's discoverability and conversion in the Apple App Store and Google Play. She researches keywords, rewrites metadata and advises on icons, screenshots and localisation to boost rankings and downloads.
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Zenith Asha steigert die Auffindbarkeit und Conversion Ihrer App im Apple App Store und bei Google Play. Sie recherchiert Keywords, überarbeitet Metadaten und berät zu Icons, Screenshots und Lokalisierung, um Rankings und Downloads zu verbessern.
              </p>

            </div>

            {/* Sixth Card - Nexus Flower */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Nexus's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-2xl overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/e155677c-efac-409b-8ec8-bb0f3ef86a9b-1767045789460.png"
                    alt="Nexus Flower portrait - Marketing Toolchain & Automation Specialist with futuristic tech interface background"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Nexus's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Nexus Flower<br />(Toolchains)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Nexus Flower<br />(Toolchains)
              </h3>

              {/* Nexus's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Marketing Toolchain & Automation Specialist
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>Spezialistin für Marketing‑Toolchains & Automatisierung


              </p>

              {/* Nexus's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>Nexus Flower audits your marketing tech stack and designs automations that connect CRM, email, analytics and ad platforms. She maps workflows, identifies bottlenecks and documents step‑by‑step solutions using no‑code tools.


              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>Nexus Flower prüft Ihren Marketing‑Tech‑Stack und entwickelt Automatisierungen, die CRM, E‑Mail, Analytik und Werbeplattformen verbinden. Sie analysiert Workflows, identifiziert Engpässe und dokumentiert Schritt‑für‑Schritt‑Lösungen mit No‑Code‑Tools.


              </p>

            </div>

            {/* Seventh Card - Lexi Juris */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Lexi's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-xl overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/1f799f8b-1508-46a8-8fe4-8ced2adc6c93-1767046387778.png"
                    alt="Lexi Juris portrait - Compliance, Accounting & Legal Advisor with futuristic digital interface background"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Lexi's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Lexi Juris<br />(Legal Consult)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Lexi Juris<br />(Legal&nbsp;Consult)
              </h3>

              {/* Lexi's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Compliance, Accounting & Legal Advisor
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>Berater für Compliance, Finanzen & Recht


              </p>

              {/* Lexi's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Lexi Juris provides high‑level guidance on accounting, tax, data protection and legal compliance. He creates checklists for VAT, privacy and contracts, summarises obligations and highlights when to consult professionals.
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Lexi Juris bietet übergreifende Beratung zu Buchhaltung, Steuern, Datenschutz und rechtlicher Compliance. Er erstellt Checklisten für Umsatzsteuer, Datenschutz und Verträge, fasst Verpflichtungen zusammen und weist darauf hin, wann professionelle Beratung erforderlich ist.
              </p>

            </div>

            {/* Eighth Card - Echo Muse */}
            <div
              className="team-card p-6 rounded-lg transition-all duration-300"
              style={{
                backgroundColor: 'rgba(3, 27, 58, 0.8)',
                border: '2px solid #00b8ff',
                boxShadow: '0 4px 6px rgba(0, 184, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = '#16c55f';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(22, 197, 95, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#00b8ff';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 184, 255, 0.2)';
              }}>

              {/* Echo's Image */}
              <div className="flex justify-center mb-4">
                <div
                  className="relative w-40 h-40 rounded-full overflow-hidden"
                  style={{
                    border: '3px solid #00b8ff',
                    boxShadow: '0 0 20px rgba(0, 184, 255, 0.6)'
                  }}>

                  <Image
                    src="/assets/images/2344d27a-297f-4fd9-a15c-7ebfc9b157ac-1767046680311.png"
                    alt="Echo Muse portrait - Brand Narrative & AI-Search Strategist with futuristic cyberpunk aesthetic"
                    fill
                    priority
                    className="object-cover" />

                </div>
              </div>

              {/* Echo's Name */}
              <h3
                data-lang="en"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Echo Muse<br />(Content Queen)
              </h3>
              <h3
                data-lang="de"
                className="text-2xl font-semibold text-center mb-2"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  color: '#16c55f',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Echo Muse<br />(Content&nbsp;Queen)
              </h3>

              {/* Echo's Role */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

                Brand Narrative & AI‑Search Strategist
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#00b8ff',
                  fontWeight: 500,
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

                Markenstory‑ & AI‑Search‑Strategin
              </p>

              {/* Echo's Description */}
              <p
                data-lang="en"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'en' ? 'block' : 'none'
                }}>

Echo Muse crafts compelling brand narratives and develops modern content strategies that perform in both AI‑generated answers and traditional search. She structures information for generative engines, applies AEO, GEO, SEO and E‑E‑A‑T best practices, and plans multi‑channel content calendars. Her goal is to increase brand recognition and discoverability through engaging storytelling and AI affinity.
              </p>
              <p
                data-lang="de"
                className="text-center mb-4"
                style={{
                  fontFamily: '"Inter", sans-serif',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  display: currentLang === 'de' ? 'block' : 'none'
                }}>

Echo Muse entwickelt überzeugende Markennarrative und moderne Content‑Strategien, die sowohl in KI‑generierten Antworten als auch in der klassischen Suche funktionieren. Sie strukturiert Informationen für generative Suchmaschinen, wendet AEO‑, GEO‑, SEO‑ und E‑E‑A‑T‑Best Practices an und plant Content‑Kalender über mehrere Kanäle hinweg. Ihr Ziel ist es, die Markenbekanntheit und Auffindbarkeit durch fesselndes Storytelling und KI‑Affinität zu steigern.
              </p>

            </div>
          </div>

          {/* CTA Button - Moved below Team section */}
          <div className="flex justify-center mt-16">
            <Link href="/contact-section">
              <button
                data-lang="en"
                className="cta px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  backgroundColor: 'transparent',
                  border: '2px solid #00b8ff',
                  color: '#00b8ff',
                  display: currentLang === 'en' ? 'inline-block' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#00b8ff';
                  e.currentTarget.style.color = '#031b3a';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 184, 255, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#00b8ff';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                Work with us
              </button>
            </Link>
            <Link href="/contact-section">
              <button
                data-lang="de"
                className="cta px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  backgroundColor: 'transparent',
                  border: '2px solid #00b8ff',
                  color: '#00b8ff',
                  display: currentLang === 'de' ? 'inline-block' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#00b8ff';
                  e.currentTarget.style.color = '#031b3a';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 184, 255, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#00b8ff';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                Mit&nbsp;uns&nbsp;arbeiten
              </button>
            </Link>
          </div>

          {/* Hero Bullet Ticker - Moved from Hero Section */}
          <div className="mt-12">
            <HeroBulletTicker lang={currentLang} containerClassName="max-w-6xl" />
          </div>
        </div>
      </section>
    </div>);

};

export default AboutInteractive;